import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Fab,
  Tooltip,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';
import { getLeadStatusChip, LEAD_STATUS_OPTIONS } from '../utils/leadStatusChip';

interface Lead {
  id: number;
  nome: string;
  email: string;
  telefone: string;
  origem: string;
  status: string;
  score: number;
  created_at: string;
  observacoes?: string;
  whatsapp_number?: string;
  city?: string;
  qualification_status?: string;
  crm_lead_id?: string;
  telefone_alternativo?: string;
  faixa_etaria?: string;
  score_pme?: number;
  bairro?: string;
  tem_whatsapp?: boolean;
  prioridade_contato?: string;
  data_1_contato?: string;
  hora_1_contato?: string;
  canal?: string;
  resposta?: string;
  interesse?: string;
  proximo_follow_up?: string;
  responsavel?: string;
  cpf_mascarado?: string;
}

interface LeadFormData {
  nome: string;
  email: string;
  telefone: string;
  origem: string;
  observacoes: string;
  whatsapp_number: string;
  city: string;
  qualification_status: string;
  crm_lead_id: string;
  telefone_alternativo: string;
  faixa_etaria: string;
  score_pme: string;
  bairro: string;
  tem_whatsapp: '' | 'sim' | 'nao';
  prioridade_contato: string;
  data_1_contato: string;
  hora_1_contato: string;
  canal: string;
  resposta: string;
  interesse: string;
  proximo_follow_up: string;
  responsavel: string;
  cpf_mascarado: string;
}

const EMPTY_LEAD_FORM: LeadFormData = {
  nome: '',
  email: '',
  telefone: '',
  origem: '',
  observacoes: '',
  whatsapp_number: '',
  city: '',
  qualification_status: '',
  crm_lead_id: '',
  telefone_alternativo: '',
  faixa_etaria: '',
  score_pme: '',
  bairro: '',
  tem_whatsapp: '',
  prioridade_contato: '',
  data_1_contato: '',
  hora_1_contato: '',
  canal: '',
  resposta: '',
  interesse: '',
  proximo_follow_up: '',
  responsavel: '',
  cpf_mascarado: '',
};

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [formData, setFormData] = useState<LeadFormData>(EMPTY_LEAD_FORM);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    leadId: 0,
    leadName: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filtros: Nome/WhatsApp usam um valor "input" (a cada tecla) e um valor
  // "filter" debounced (o que de fato vai pro backend), pra não disparar uma
  // request por tecla digitada. Status é discreto, aplica na hora.
  const [nomeInput, setNomeInput] = useState('');
  const [whatsappInput, setWhatsappInput] = useState('');
  const [nomeFilter, setNomeFilter] = useState('');
  const [whatsappFilter, setWhatsappFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Backend exige Bearer token nos endpoints /leads/* (antes disso não exigia).
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setNomeFilter(nomeInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [nomeInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setWhatsappFilter(whatsappInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [whatsappInput]);

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  useEffect(() => {
    fetchLeads(page, rowsPerPage, nomeFilter, whatsappFilter, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, nomeFilter, whatsappFilter, statusFilter]);

  const fetchLeads = async (
    pageArg: number = page,
    rowsPerPageArg: number = rowsPerPage,
    nomeArg: string = nomeFilter,
    whatsappArg: string = whatsappFilter,
    statusArg: string = statusFilter
  ) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/leads/`, {
        headers: getAuthHeaders(),
        params: {
          skip: pageArg * rowsPerPageArg,
          limit: rowsPerPageArg,
          ...(nomeArg.trim() ? { nome: nomeArg.trim() } : {}),
          ...(whatsappArg.trim() ? { whatsapp: whatsappArg.trim() } : {}),
          ...(statusArg ? { status: statusArg } : {}),
        },
      });
      setLeads(response.data);
    } catch (error) {
      showSnackbar('Erro ao carregar leads', 'error');
    }
    setLoading(false);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (leadToEdit?: Lead) => {
    if (leadToEdit) {
      setEditingLead(leadToEdit);
      setFormData({
        nome: leadToEdit.nome,
        email: leadToEdit.email || '',
        telefone: leadToEdit.telefone,
        origem: leadToEdit.origem,
        observacoes: leadToEdit.observacoes || '',
        whatsapp_number: leadToEdit.whatsapp_number || '',
        city: leadToEdit.city || '',
        qualification_status: leadToEdit.qualification_status || '',
        crm_lead_id: leadToEdit.crm_lead_id || '',
        telefone_alternativo: leadToEdit.telefone_alternativo || '',
        faixa_etaria: leadToEdit.faixa_etaria || '',
        score_pme: leadToEdit.score_pme !== undefined && leadToEdit.score_pme !== null ? String(leadToEdit.score_pme) : '',
        bairro: leadToEdit.bairro || '',
        tem_whatsapp: leadToEdit.tem_whatsapp === true ? 'sim' : leadToEdit.tem_whatsapp === false ? 'nao' : '',
        prioridade_contato: leadToEdit.prioridade_contato || '',
        data_1_contato: leadToEdit.data_1_contato || '',
        hora_1_contato: leadToEdit.hora_1_contato || '',
        canal: leadToEdit.canal || '',
        resposta: leadToEdit.resposta || '',
        interesse: leadToEdit.interesse || '',
        proximo_follow_up: leadToEdit.proximo_follow_up || '',
        responsavel: leadToEdit.responsavel || '',
        cpf_mascarado: leadToEdit.cpf_mascarado || '',
      });
    } else {
      setEditingLead(null);
      setFormData(EMPTY_LEAD_FORM);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingLead(null);
    setFormData(EMPTY_LEAD_FORM);
  };

  const getLocationInfo = async () => {
    try {
      const response = await axios.get('https://ipapi.co/json/');
      return {
        ip_address: response.data.ip,
        location: `${response.data.latitude},${response.data.longitude}`,
        city: response.data.city,
        state: response.data.region,
        country: response.data.country_name,
      };
    } catch (error) {
      console.error('Erro ao obter informações de localização:', error);
      return {};
    }
  };

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    let deviceType = 'desktop';
    
    if (/mobile/i.test(userAgent)) {
      deviceType = 'mobile';
    } else if (/tablet/i.test(userAgent)) {
      deviceType = 'tablet';
    }
    
    return {
      browser: userAgent,
      device_type: deviceType,
      operating_system: navigator.platform,
      referrer: document.referrer,
    };
  };

  const openWhatsApp = (phoneNumber: string) => {
    const formattedNumber = phoneNumber.replace(/\D/g, '');
    const message = encodeURIComponent(`Olá! Gostaria de mais informações sobre os planos de saúde da MedPrime Benefícios.`);
    
    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.location.href = `whatsapp://send?phone=55${formattedNumber}&text=${message}`;
    } else {
      window.open(`https://web.whatsapp.com/send?phone=55${formattedNumber}&text=${message}`, '_blank');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const locationInfo = await getLocationInfo();
      const browserInfo = getBrowserInfo();

      const scorePmeParsed = formData.score_pme.trim() === ''
        ? undefined
        : parseFloat(formData.score_pme.replace(',', '.'));

      const leadData: Record<string, unknown> = {
        ...formData,
        ...locationInfo,
        ...browserInfo,
        email: formData.email.trim() === '' ? undefined : formData.email.trim(),
        whatsapp_number: formData.whatsapp_number.trim() || formData.telefone,
        score_pme: scorePmeParsed !== undefined && !Number.isNaN(scorePmeParsed) ? scorePmeParsed : undefined,
        tem_whatsapp: formData.tem_whatsapp === '' ? undefined : formData.tem_whatsapp === 'sim',
      };

      if (editingLead) {
        await axios.put(`${API_URL}/leads/${editingLead.id}`, leadData, { headers: getAuthHeaders() });
        showSnackbar('Lead atualizado com sucesso!', 'success');
      } else {
        await axios.post(`${API_URL}/leads/`, leadData, { headers: getAuthHeaders() });
        showSnackbar('Lead criado com sucesso!', 'success');
        // Abrir WhatsApp após criar novo lead
        openWhatsApp(formData.telefone);
      }

      handleCloseModal();
      fetchLeads(page, rowsPerPage);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao salvar lead';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/leads/${deleteDialog.leadId}`, { headers: getAuthHeaders() });
      showSnackbar('Lead removido com sucesso!', 'success');
      setDeleteDialog({ open: false, leadId: 0, leadName: '' });
      fetchLeads(page, rowsPerPage);
    } catch (error) {
      showSnackbar('Erro ao remover lead', 'error');
    }
    setLoading(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading && leads.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gerenciamento de Leads
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gerencie os leads capturados pelo sistema
        </Typography>
      </Box>

      {/* Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{leads.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Leads nesta página
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {leads.filter(l => l.status.toLowerCase() === 'convertido').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Convertidos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ScheduleIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {leads.filter(l => l.status.toLowerCase() === 'em_contato').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contatado
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocationIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {Array.from(new Set(leads.map(l => l.origem))).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Origens Diferentes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Buscar por nome"
              size="small"
              fullWidth
              value={nomeInput}
              onChange={(e) => setNomeInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Buscar por WhatsApp"
              size="small"
              fullWidth
              value={whatsappInput}
              onChange={(e) => setWhatsappInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl size="small" fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value as string)}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {LEAD_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de Leads */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>WhatsApp</TableCell>
                <TableCell>Cidade</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Classificação</TableCell>
                <TableCell>Prioridade</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Data de Criação</TableCell>
                <TableCell
                  align="center"
                  sx={{
                    position: 'sticky',
                    right: 0,
                    zIndex: 3,
                    backgroundColor: 'background.paper',
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.08)',
                  }}
                >
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {lead.nome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ID: {lead.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {lead.email}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {lead.telefone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {lead.whatsapp_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {lead.city || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={lead.origem} size="small" />
                  </TableCell>
                  <TableCell>
                    {getLeadStatusChip(lead.status)}
                  </TableCell>
                  <TableCell>
                    {lead.qualification_status || '-'}
                  </TableCell>
                  <TableCell>
                    {lead.prioridade_contato || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lead.score}
                      size="small" 
                      color={lead.score >= 7 ? 'success' : lead.score >= 4 ? 'warning' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(lead.created_at)}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      position: 'sticky',
                      right: 0,
                      zIndex: 2,
                      backgroundColor: 'background.paper',
                      boxShadow: '-2px 0 4px rgba(0,0,0,0.08)',
                    }}
                  >
                    <Tooltip title="Editar lead">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenModal(lead)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Abrir WhatsApp">
                      <IconButton
                        color="success"
                        onClick={() => openWhatsApp(lead.telefone)}
                        size="small"
                      >
                        <WhatsAppIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir lead">
                      <IconButton
                        color="error"
                        onClick={() => setDeleteDialog({
                          open: true,
                          leadId: lead.id,
                          leadName: lead.nome,
                        })}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={-1}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        />
      </Paper>

      {/* Botão FAB para adicionar */}
      <Tooltip title="Adicionar lead">
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => handleOpenModal()}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Modal de Criação/Edição */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLead ? 'Editar Lead' : 'Novo Lead'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Contato
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Nome Completo"
                  fullWidth
                  variant="outlined"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Telefone"
                  fullWidth
                  variant="outlined"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="WhatsApp"
                  fullWidth
                  variant="outlined"
                  helperText="Se vazio, usa o mesmo valor do Telefone"
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Telefone Alternativo"
                  fullWidth
                  variant="outlined"
                  value={formData.telefone_alternativo}
                  onChange={(e) => setFormData({ ...formData, telefone_alternativo: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel>Origem</InputLabel>
                  <Select
                    value={formData.origem}
                    label="Origem"
                    onChange={(e) => setFormData({ ...formData, origem: e.target.value })}
                  >
                    <MenuItem value="website">Website</MenuItem>
                    <MenuItem value="chatbot">Chatbot</MenuItem>
                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                    <MenuItem value="telefone">Telefone</MenuItem>
                    <MenuItem value="indicacao">Indicação</MenuItem>
                    <MenuItem value="outros">Outros</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Cidade"
                  fullWidth
                  variant="outlined"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Bairro"
                  fullWidth
                  variant="outlined"
                  value={formData.bairro}
                  onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel>Tem WhatsApp</InputLabel>
                  <Select
                    value={formData.tem_whatsapp}
                    label="Tem WhatsApp"
                    onChange={(e) => setFormData({ ...formData, tem_whatsapp: e.target.value as '' | 'sim' | 'nao' })}
                  >
                    <MenuItem value=""><em>Não informado</em></MenuItem>
                    <MenuItem value="sim">Sim</MenuItem>
                    <MenuItem value="nao">Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              CRM / Planilha
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="ID Lead (CRM)"
                  fullWidth
                  variant="outlined"
                  value={formData.crm_lead_id}
                  onChange={(e) => setFormData({ ...formData, crm_lead_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Classificação"
                  fullWidth
                  variant="outlined"
                  value={formData.qualification_status}
                  onChange={(e) => setFormData({ ...formData, qualification_status: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Prioridade de Contato"
                  fullWidth
                  variant="outlined"
                  value={formData.prioridade_contato}
                  onChange={(e) => setFormData({ ...formData, prioridade_contato: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Faixa Etária"
                  fullWidth
                  variant="outlined"
                  value={formData.faixa_etaria}
                  onChange={(e) => setFormData({ ...formData, faixa_etaria: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Canal"
                  fullWidth
                  variant="outlined"
                  value={formData.canal}
                  onChange={(e) => setFormData({ ...formData, canal: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Score PME"
                  fullWidth
                  variant="outlined"
                  value={formData.score_pme}
                  onChange={(e) => setFormData({ ...formData, score_pme: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="CPF Mascarado"
                  fullWidth
                  variant="outlined"
                  value={formData.cpf_mascarado}
                  onChange={(e) => setFormData({ ...formData, cpf_mascarado: e.target.value })}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Follow-up
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Data 1º Contato"
                  fullWidth
                  variant="outlined"
                  value={formData.data_1_contato}
                  onChange={(e) => setFormData({ ...formData, data_1_contato: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Hora 1º Contato"
                  fullWidth
                  variant="outlined"
                  value={formData.hora_1_contato}
                  onChange={(e) => setFormData({ ...formData, hora_1_contato: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Próximo Follow-up"
                  fullWidth
                  variant="outlined"
                  value={formData.proximo_follow_up}
                  onChange={(e) => setFormData({ ...formData, proximo_follow_up: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Interesse"
                  fullWidth
                  variant="outlined"
                  value={formData.interesse}
                  onChange={(e) => setFormData({ ...formData, interesse: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Responsável"
                  fullWidth
                  variant="outlined"
                  value={formData.responsavel}
                  onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  label="Resposta"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  value={formData.resposta}
                  onChange={(e) => setFormData({ ...formData, resposta: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              margin="dense"
              label="Observações"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : (editingLead ? 'Atualizar' : 'Criar')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, leadId: 0, leadName: '' })}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o lead <strong>{deleteDialog.leadName}</strong>?
          </DialogContentText>
          <DialogContentText variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, leadId: 0, leadName: '' })}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Leads; 